import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from '@tanstack/react-router';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { queryClient } from '@/lib/query-client';
import { Toaster } from './components/ui/sonner';
import { MeProvider } from '@/lib/me-provider';
import { RevocationWatcher } from '@/lib/auth/revocation-watcher';
import { AuthProvider, useAuth } from '@/lib/auth/use-auth';

// Waits for auth bootstrap before mounting the router, so `beforeLoad` guards
// never see the transient 'initializing' phase.
function AppRouter() {
  const auth = useAuth();
  const { t } = useTranslation();

  // Sole reactive bridge between auth and routing: re-runs `beforeLoad` guards on
  // every auth phase change (login, logout, session expiry). Guards are the only
  // place that navigates on auth changes — nothing else does.
  useEffect(() => {
    if (auth.phase === 'initializing') {
      return;
    }
    void router.invalidate();
  }, [auth.phase]);

  if (auth.phase === 'initializing') {
    return <div className='flex h-svh items-center justify-center text-muted-foreground'>{t('common:loading')}</div>;
  }

  return <RouterProvider router={router} context={{ queryClient, auth, ensureProfile: auth.ensureProfile }} />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <MeProvider>
          <RevocationWatcher />
          <AppRouter />

          {/* ui/sonner.tsx defaults to theme="system" (next-themes) but spreads
              {...props} after its own theme, so pinning it here overrides that
              without editing the vendored file. */}
          <Toaster position='top-right' theme='light' />
          <ReactQueryDevtools buttonPosition='bottom-right' initialIsOpen={false} />
        </MeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
